import React, { useState } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';

interface SuccessModalProps {
  title: string;
  message: string;
  onClose: () => void;
  showButton?: boolean;
  buttonText?: string;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

export function SuccessModal({ 
  title, 
  message, 
  onClose, 
  showButton = false,
  buttonText = '确认',
  autoClose = true,
  autoCloseDelay = 2000
}: SuccessModalProps) {
  const [isClosing, setIsClosing] = useState(false);

  React.useEffect(() => {
    if (!autoClose) return;
    const timer = setTimeout(() => {
      setIsClosing(true);
      setTimeout(onClose, 300);
    }, autoCloseDelay);
    return () => clearTimeout(timer);
  }, [autoClose, autoCloseDelay, onClose]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  const dismiss = useModalDismiss(handleClose);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'var(--modal-mask)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001,
      backdropFilter: 'blur(4px)', animation: isClosing ? 'fadeOut 0.3s ease-in forwards' : 'fadeIn 0.3s ease-out'
    }} {...dismiss}>
      <div style={{
        backgroundColor: 'var(--card-bg)', padding: 40, borderRadius: 24, width: 420,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', color: 'var(--text-color)',
        textAlign: 'center', animation: isClosing ? 'scaleOut 0.3s ease-in forwards' : 'scaleIn 0.3s ease-out'
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✨</div>
        <h2 style={{ marginBottom: 12, fontSize: 18, fontWeight: 800 }}>{title}</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: showButton ? 28 : 0 }}>
          {message}
        </p>
        {showButton && (
          <button
            onClick={handleClose}
            style={{
              padding: '12px 48px', backgroundColor: 'var(--primary-color)', color: 'white',
              border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 700,
              boxShadow: '0 10px 15px -3px rgba(24, 144, 255, 0.4)', transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 15px 25px -3px rgba(24, 144, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(24, 144, 255, 0.4)';
            }}
          >
            {buttonText}
          </button>
        )}
      </div>
      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes scaleOut {
          from {
            transform: scale(1);
            opacity: 1;
          }
          to {
            transform: scale(0.9);
            opacity: 0;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
